import type { NormalizedSession } from "./types.js";
import type { StatsCache } from "./stats-cache.js";
import { getDailyTokens, getModelTokenRatios } from "./stats-cache.js";

/**
 * Derive the date string (YYYY-MM-DD) from a session's start timestamp.
 */
function sessionDate(session: NormalizedSession): string {
  return new Date(session.sessionStart).toISOString().slice(0, 10);
}

/**
 * Normalize model names for matching between JSONL sessions and stats-cache.
 * Stats-cache uses full model IDs (e.g., "claude-opus-4-5-20251101"),
 * while sessions may use shorter names (e.g., "claude-opus-4-5").
 */
function modelMatches(sessionModel: string, cacheModel: string): boolean {
  return (
    sessionModel === cacheModel ||
    cacheModel.startsWith(sessionModel) ||
    sessionModel.startsWith(cacheModel)
  );
}

export interface AttributionResult {
  /** Number of sessions upgraded from tokenizer/none to stats-cache */
  upgraded: number;
  /** Number of sessions left unchanged (already had API data) */
  unchanged: number;
  /** Number of sessions that couldn't be attributed (no stats-cache data for that day) */
  unattributed: number;
}

/**
 * Apply proportional token attribution from stats-cache daily totals.
 *
 * For each day+model combination, distributes the stats-cache daily total
 * across sessions proportionally based on their tokenizer-estimated sizes.
 * Sessions with real API usage data are excluded from redistribution.
 *
 * Mutates sessions in-place and returns attribution stats.
 */
export function applyStatsAttribution(
  sessions: NormalizedSession[],
  cache: StatsCache,
): AttributionResult {
  const result: AttributionResult = {
    upgraded: 0,
    unchanged: 0,
    unattributed: 0,
  };

  // Group sessions by date+model
  const groups = new Map<string, NormalizedSession[]>();
  for (const session of sessions) {
    if (session.tokenSource === "api") {
      result.unchanged++;
      continue;
    }

    const date = sessionDate(session);
    const key = `${date}:${session.model}`;
    const group = groups.get(key) ?? [];
    group.push(session);
    groups.set(key, group);
  }

  for (const [key, group] of groups) {
    const [date, model] = key.split(":", 2);

    // Find matching daily tokens from stats-cache
    const dailyTokens = getDailyTokens(cache, date);
    let dailyTotal = 0;

    // Find the matching model in stats-cache (handles name variations)
    for (const [cacheModel, tokens] of Object.entries(dailyTokens)) {
      if (modelMatches(model, cacheModel)) {
        dailyTotal = tokens;
        break;
      }
    }

    if (dailyTotal === 0) {
      result.unattributed += group.length;
      continue;
    }

    // Calculate total tokenizer-estimated tokens for the group
    const groupTotal = group.reduce(
      (sum, s) => sum + s.inputTokens + s.outputTokens,
      0,
    );

    if (groupTotal === 0) {
      // All sessions have 0 tokens — distribute evenly
      const perSession = Math.round(dailyTotal / group.length);
      const ratios = getModelTokenRatios(cache, model);
      for (const session of group) {
        session.inputTokens = Math.round(perSession * ratios.inputRatio);
        session.outputTokens = Math.round(perSession * ratios.outputRatio);
        session.tokenSource = "stats-cache";
        result.upgraded++;
      }
      continue;
    }

    // Proportional distribution: each session gets (its share / groupTotal) * dailyTotal
    const ratios = getModelTokenRatios(cache, model);
    for (const session of group) {
      const sessionTotal = session.inputTokens + session.outputTokens;
      const proportion = sessionTotal / groupTotal;
      const attributed = Math.round(dailyTotal * proportion);

      session.inputTokens = Math.round(attributed * ratios.inputRatio);
      session.outputTokens = Math.round(attributed * ratios.outputRatio);
      session.tokenSource = "stats-cache";
      result.upgraded++;
    }
  }

  return result;
}
